import React, { useState, useEffect } from "react";
import { Modal, Form, Input, DatePicker, Select, message, Divider } from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  FileProtectOutlined,
  CalendarOutlined,
  UserSwitchOutlined,
  BankOutlined,
  NumberOutlined,
  FileDoneOutlined,
} from "@ant-design/icons";
import logger from "../../utils/logger";
import axiosInstance from "../../utils/axios.config";
import locale from "antd/es/date-picker/locale/en_US";
import { format } from "date-fns";
import Swal from "sweetalert2";

const { Option } = Select;
const { TextArea } = Input;

const AddClientModal = ({ visible: open, onClose, onSuccess, initialName = "" }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    power_of_attorney_date: null,
  });

  const handleDateChange = (date, dateString) => {
    setFormData((prev) => ({
      ...prev,
      power_of_attorney_date: date,
    }));
  };
  
  // تحديث حقل الاسم عند تغيير خاصية initialName
  useEffect(() => {
    if (initialName) {
      form.setFieldsValue({ name: initialName });
    }
  }, [initialName, form]);
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // التحقق من وجود القيم المطلوبة
      if (!values.name || !values.national_id || !values.client_type) {
        throw new Error("الرجاء تعبئة جميع الحقول المطلوبة");
      }

      // Show loading indicator
      Swal.fire({
        title: "جاري الحفظ...",
        text: "يرجى الانتظار",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const formData = {
        name: values.name?.trim(),
        client_type: values.client_type,
        national_id: values.national_id?.trim(),
        phone: values.phone?.trim(),
        email: values.email?.trim() || null,
        address: values.address?.trim() || null,
        power_of_attorney_number:
          values.power_of_attorney_number?.trim() || null,
        power_of_attorney_date: values.power_of_attorney_date
          ? format(new Date(values.power_of_attorney_date), "yyyy-MM-dd")
          : null,
        power_of_attorney_record:
          values.power_of_attorney_record?.trim() || null,
        notary_name: values.notary_name?.trim() || null,
        notes: values.notes?.trim() || null,
        status: values.status || "نشط",
      };

      const response = await axiosInstance.post("/clients", formData);

      if (response.data.success) {
        // Show success message with SweetAlert2
        await Swal.fire({
          title: "تم الإضافة!",
          text: "تم إضافة العميل بنجاح",
          icon: "success",
          timer: 1500,
        });

        form.resetFields();
        setFormData({ power_of_attorney_date: null }); // إعادة تعيين التاريخ
        if (onSuccess) onSuccess(response.data); // تمرير بيانات العميل المنشأة
        onClose();
      }
    } catch (error) {
      logger.error("Error details:", error.response || error);

      // إظهار رسالة الخطأ بتفاصيل أكثر
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "حدث خطأ في إضافة العميل";

      Swal.fire({
        title: "خطأ!",
        text: errorMessage,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div className="text-lg md:text-xl font-bold text-right text-gray-800 mb-2 md:mb-4">
          <UserOutlined className="ml-2 text-primary" />
          إضافة عميل جديد
        </div>
      }
      open={open}
      onCancel={onClose}
      okText="إضافة العميل"
      cancelText="إلغاء"
      onOk={() => form.submit()}
      confirmLoading={loading}
      width="95%"
      style={{ maxWidth: "800px" }}
      dir="rtl"
      className="custom-modal responsive-modal"
      styles={{
        body: { maxHeight: "80vh", overflow: "auto", padding: "15px" },
        mask: { backgroundColor: "rgba(0, 0, 0, 0.45)" }
      }}
    >
      <Form
        form={form}
        dir="rtl"
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          client_type: "مدعي",
          power_of_attorney_date: formData.power_of_attorney_date,
        }}
        className="px-2 md:px-4"
        requiredMark={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
          {/* 1. اسم العميل */}
          <Form.Item
            name="name"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                اسم العميل
              </span>
            }
            rules={[{ required: true, message: "الرجاء إدخال اسم العميل" }]}
            className="mb-2 md:mb-3"
          >
            <Input
              prefix={<UserOutlined className="ml-2 text-primary" />}
              placeholder="أدخل اسم العميل بالكامل"
              className="h-10 md:h-11 text-right"
              size="middle"
            />
          </Form.Item>

          {/* 2. رقم الهوية */}
          <Form.Item
            name="national_id"
            label={
              <span className="text-gray-800 font-semibold">رقم الهوية</span>
            }
            rules={[{ required: true, message: "الرجاء إدخال رقم الهوية" }]}
            className="mb-3"
          >
            <Input
              prefix={<NumberOutlined className="ml-2 text-primary" />}
              placeholder="أدخل رقم الهوية"
              className="text-right h-11"
              size="large"
              maxLength={10}
            />
          </Form.Item>

          {/* 3. رقم الجوال */}
          <Form.Item
            name="phone"
            label={
              <span className="text-gray-800 font-semibold">رقم الجوال</span>
            }
            rules={[
              { required: true, message: "الرجاء إدخال رقم الجوال" },
              {
                pattern: /^07\d{9}$/,
                message: "يجب أن يبدأ الرقم بـ 07 ويتكون من 11 أرقام",
              },
            ]}
            className="mb-3"
          >
            <Input
              prefix={<PhoneOutlined className="ml-2 text-primary" />}
              placeholder="07xxxxxxxxx"
              className="text-right h-11"
              size="large"
              maxLength={11}
            />
          </Form.Item>

          {/* 4. البريد الإلكتروني */}
          <Form.Item
            name="email"
            label={
              <span className="text-gray-800 font-semibold">
                البريد الإلكتروني
              </span>
            }
            rules={[
              { type: "email", message: "صيغة البريد الإلكتروني غير صحيحة" },
            ]}
            className="mb-3"
          >
            <Input
              prefix={<MailOutlined className="ml-2 text-primary" />}
              placeholder="example@domain.com"
              className="text-right h-11"
              size="large"
              dir="rtl"
            />
          </Form.Item>

          {/* 5. العنوان */}
          <Form.Item
            name="address"
            label={<span className="text-gray-800 font-semibold">العنوان</span>}
            className="mb-3"
          >
            <Input
              prefix={<HomeOutlined className="ml-2 text-primary" />}
              placeholder="أدخل العنوان التفصيلي"
              className="text-right h-11"
              size="large"
            />
          </Form.Item>

          {/* 6. نوع العميل */}
          <Form.Item
            name="client_type"
            label={
              <span className="text-gray-800 font-semibold">نوع العميل</span>
            }
            rules={[{ required: true, message: "الرجاء اختيار نوع العميل" }]}
            className="mb-3"
          >
            <Select
              placeholder="اختر نوع العميل"
              className="text-right h-11"
              size="large"
              suffixIcon={<UserSwitchOutlined className="text-primary" />}
            >
              <Option value="مدعي">مدعي</Option>
              <Option value="مدعى عليه">مدعى عليه</Option>
            </Select>
          </Form.Item>

          {/* 7. رقم الوكالة */}
          <Form.Item
            name="power_of_attorney_number"
            label={
              <span className="text-gray-800 font-semibold">رقم الوكالة</span>
            }
            className="mb-3"
          >
            <Input
              prefix={<FileDoneOutlined className="ml-2 text-primary" />}
              placeholder="أدخل رقم الوكالة"
              className="text-right h-11"
              size="large"
            />
          </Form.Item>

          {/* 8. سجل الوكالة */}
          <Form.Item
            name="power_of_attorney_record"
            label={
              <span className="text-gray-800 font-semibold">سجل الوكالة</span>
            }
            className="mb-3"
          >
            <Input
              prefix={<FileProtectOutlined className="ml-2 text-primary" />}
              placeholder="أدخل رقم سجل الوكالة"
              className="text-right h-11"
              size="large"
            />
          </Form.Item>

          {/* 9. تاريخ الوكالة */}
          <Form.Item
            name="power_of_attorney_date"
            label={
              <span className="text-gray-800 font-semibold">تاريخ الوكالة</span>
            }
            className="mb-3"
          >
            <DatePicker
              placeholder="اختر تاريخ الوكالة"
              style={{ width: "100%" }}
              format="YYYY/MM/DD"
              locale={locale}
              className="text-right h-11"
              size="large"
              value={formData.power_of_attorney_date}
              onChange={handleDateChange}
              suffixIcon={<CalendarOutlined className="text-primary" />}
            />
          </Form.Item>

          {/* 10. كاتب العدل */}
          <Form.Item
            name="notary_name"
            label={
              <span className="text-gray-800 font-semibold">كاتب العدل</span>
            }
            className="mb-3"
          >
            <Input
              prefix={<BankOutlined className="ml-2 text-primary" />}
              placeholder="أدخل اسم كاتب العدل"
              className="text-right h-11"
              size="large"
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default AddClientModal;
